import { motion } from "framer-motion";
import { Lock, ShieldCheck, BadgeCheck, Landmark, Award } from "lucide-react";

/**
 * Trust strip — accepted payments + credentials + security seals.
 * Sits between hero and Approach section to lock in credibility above the fold.
 */
export default function TrustStrip() {
  return (
    <section
      className="relative bg-white border-y border-[#0A192F]/10"
      data-testid="trust-strip"
    >
      <div className="max-w-[1500px] mx-auto px-5 sm:px-8 lg:px-10 py-10 md:py-12 grid md:grid-cols-3 gap-10 lg:gap-16 items-center">
        {/* Left — Credentials */}
        <div className="flex flex-col gap-4">
          <div className="label-mini">Credentials</div>
          <div className="flex flex-wrap gap-3">
            <Credential icon={<ShieldCheck className="w-4 h-4" strokeWidth={1.4} />} label="Trusted Local Provider" />
            <Credential icon={<BadgeCheck className="w-4 h-4" strokeWidth={1.4} />} label="Licensed & Insured" />
            <Credential icon={<Award className="w-4 h-4" strokeWidth={1.4} />} label="5★ Google Rated" />
          </div>
        </div>

        {/* Middle — Payments */}
        <div className="flex flex-col gap-4 md:items-center">
          <div className="label-mini">Accepted Payments</div>
          <div className="flex items-center gap-3 flex-wrap">
            <PayLogo><VisaMark /></PayLogo>
            <PayLogo><MCMark /></PayLogo>
            <PayLogo><AmexMark /></PayLogo>
            <PayLogo><DiscoverMark /></PayLogo>
            <PayLogo><ZelleMark /></PayLogo>
            <PayLogo><CashAppMark /></PayLogo>
            <PayLogo><CashMark /></PayLogo>
          </div>
        </div>

        {/* Right — Security */}
        <div className="flex flex-col gap-4 md:items-end">
          <div className="label-mini">Secure &amp; Trusted</div>
          <div className="flex flex-wrap gap-3">
            <Credential icon={<Lock className="w-4 h-4" strokeWidth={1.4} />} label="Encrypted forms" />
            <Credential icon={<Landmark className="w-4 h-4" strokeWidth={1.4} />} label="PA Notary Commissioned" />
          </div>
        </div>
      </div>
    </section>
  );
}

function Credential({ icon, label }) {
  return (
    <motion.span
      whileHover={{ y: -2 }}
      transition={{ duration: 0.3, ease: [0.7, 0, 0.2, 1] }}
      className="inline-flex items-center gap-2 px-3 py-2 border border-[#0A192F]/12 bg-[#FBF9F5] text-[#0A192F] text-[11px] tracking-wide rounded-full"
    >
      <span className="text-[#C5A059]">{icon}</span>
      <span className="whitespace-nowrap">{label}</span>
    </motion.span>
  );
}

function PayLogo({ children }) {
  return (
    <div className="w-14 h-9 md:w-16 md:h-10 bg-white border border-[#0A192F]/12 rounded-md flex items-center justify-center text-[#0A192F]">
      {children}
    </div>
  );
}

/* Minimal payment marks — text based, no external assets required */
function VisaMark() {
  return <span className="font-mono-tab italic font-bold text-[13px] tracking-tight text-[#1a1f71]">VISA</span>;
}
function MCMark() {
  return (
    <span className="flex items-center">
      <span className="w-3.5 h-3.5 rounded-full bg-[#eb001b] -mr-1.5" />
      <span className="w-3.5 h-3.5 rounded-full bg-[#f79e1b] mix-blend-multiply" />
    </span>
  );
}
function AmexMark() {
  return <span className="font-mono-tab font-bold text-[11px] tracking-tight text-white bg-[#006fcf] px-1.5 py-0.5 rounded-sm">AMEX</span>;
}
function DiscoverMark() {
  return <span className="font-mono-tab font-semibold text-[10px] tracking-tight text-[#0A192F]">DISC<span className="text-[#ff6000]">●</span>VER</span>;
}
function CashMark() {
  return <span className="font-serif text-[13px] italic text-[#0A192F]">Cash</span>;
}
function ZelleMark() {
  return <span className="font-mono-tab font-semibold text-[10px] tracking-tight text-white bg-[#6d1ed4] px-1.5 py-0.5 rounded-sm">Zelle</span>;
}
function CashAppMark() {
  return <span className="font-mono-tab font-bold text-[11px] tracking-tight text-white bg-[#00d632] px-1.5 py-0.5 rounded-sm">Cash App</span>;
}
